import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { Typography, TextField, Select, MenuItem, Switch, FormControlLabel } from '@mui/material';
import { Success, Failed, Loader } from '../../components';
import {
  ProfilePageShell,
  ProfileContent,
  ProfileHead,
  ProfileSection,
  ProfileGrid,
  ProfileField,
  ProfilePhoneField,
  profileInputSx as inputSx,
} from '../../components/ProfileLayout';
import { myProfileValidationSchema } from '../auth/helper/Schema';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { PLAYBACK_SPEEDS, isValidPlaybackRate } from '../../Utils/playbackPreference';
import { getProfileDetails } from '../../rtk-apis/profile/getProfileDetails';
import { updateProfileDetails } from '../../rtk-apis/profile/updateProfileDetails';
import { getUserDocApi } from '../../rtk-apis/auth/getUserDoc';
import { clearState } from '../../store/slices/profile/updateProfileDetails';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* ════════════════════════════════════════════════════════════════════════
   My Profile — MUI rewrite on the shared cardiac theme (navy + teal).
   Was legacy dashboard.scss form markup (inconsistent field styling across
   roles). Logic is unchanged: same Formik + Yup schema, same role-aware data
   source (Admin vs others), same role-gated switch-role toggle, same country
   code + contact handling. Only presentation moved to MUI.
   ════════════════════════════════════════════════════════════════════════ */

const MyProfile = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [AdminDetails, setAdminDetails] = useState<any>([]);
  const [displayMessage, setDisplayMessage] = useState('');
  const [OpenSuccessModal, setOpenSuccessModal] = useState(false);
  const [OpenFailureModal, setOpenFailureModal] = useState(false);
  const IsClinicAdmin =
    sessionStorage?.getItem('role') === 'Admin' || localStorage?.getItem('role') === 'Admin';
  const isSonographer =
    sessionStorage?.getItem('role') === 'Sonographer' ||
    localStorage?.getItem('role') === 'Sonographer';
  const [switchToAdmin, setSwitchToAdmin] = useState(false);

  const [countryCode, setCountryCode] = useState('+61');

  // ****************************** Redux States *****************************************
  const { status, data, loading } = useSelector(
    (state: RootState) => state.getProfileDetailsResponse
  );
  /** The same payload, read as the object it actually is. */
  const profile = data as unknown as { defaultPlaybackRate?: unknown } | undefined;

  const {
    status: updateProfileStatus,
    msg: updateProfileMessage,
    loading: updateProfileLoading,
  } = useSelector((state: RootState) => state.updateProfileDetailsResponse);

  const {
    data: switchRoleData,
    status: switchRoleStatus,
    msg: switchRoleMessage,
    loading: switchRoleLoading,
    accessToken,
    pacsResult: switchPacsResult,
  } = useSelector((state: RootState) => state.getUserDocResponse);

  // ************************ Use-Effects *****************************************
  useEffect(() => {
    if (status === 'SUCCESS' && data?.adminDetails?.length > 0) {
      setAdminDetails(data?.adminDetails[0]);
    }
  }, [status, data]);

  // Show the saved code; save sends this value, so a stale default would overwrite it.
  useEffect(() => {
    const saved = IsClinicAdmin
      ? AdminDetails?.countryCode
      : (data as unknown as { countryCode?: string } | undefined)?.countryCode;
    if (saved) {
      setCountryCode(saved);
    }
  }, [IsClinicAdmin, AdminDetails, data]);

  useEffect(() => {
    const payload = {
      email: sessionStorage.getItem('email') || localStorage.getItem('email'),
      loggedInId: sessionStorage.getItem('userId') || localStorage.getItem('userId'),
      hospitalId: sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId'),
    };
    dispatch(getUserDocApi(payload));
  }, []);

  const myProfileInitialValues = {
    name: IsClinicAdmin ? AdminDetails?.name : data?.name,
    role: data?.role ?? '',
    email: IsClinicAdmin ? AdminDetails?.email : data?.email,
    contactNumber: IsClinicAdmin ? AdminDetails?.contactNo : data?.contactDetails,
    countryCode: IsClinicAdmin ? AdminDetails?.countryCode : data?.countryCode,
    // '' = no preference; the viewer then slows Doppler loops and leaves the rest at 1x.
    // Cast because the profile slice types its payload as `string`; every other field
    // here reads through the same loose shape.
    defaultPlaybackRate: isValidPlaybackRate(profile?.defaultPlaybackRate)
      ? String(profile?.defaultPlaybackRate)
      : '',
  };

  useEffect(() => {
    getLoggedInUserProfileDetails();
  }, [updateProfileStatus]);

  const getLoggedInUserProfileDetails = () => {
    const userEmail = sessionStorage.getItem('email') || localStorage.getItem('email');
    dispatch(getProfileDetails({ email: userEmail }));
  };

  const handleSubmit = (values: any) => {
    const payload = IsClinicAdmin
      ? {
          adminDetails: [
            {
              name: values?.name,
              email: values?.email,
              contactNo: values?.contactNumber,
              countryCode: countryCode,
            },
          ],
        }
      : {
          name: values?.name,
          email: values?.email,
          contactDetails: values?.contactNumber,
          countryCode: countryCode,
          // null clears the preference, so a user can go back to the viewer's own
          // behaviour rather than being stuck with a speed they once picked.
          defaultPlaybackRate: values?.defaultPlaybackRate
            ? Number(values.defaultPlaybackRate)
            : null,
        };
    dispatch(updateProfileDetails(payload));
  };

  useEffect(() => {
    const handleSuccess = message => {
      setDisplayMessage(message);
      setOpenSuccessModal(true);
      setTimeout(() => {
        setOpenSuccessModal(false);
        setDisplayMessage('');
        getLoggedInUserProfileDetails();
      }, 1500);
    };

    const handleFailure = message => {
      setDisplayMessage(message);
      setOpenFailureModal(true);
      setTimeout(() => {
        setOpenFailureModal(false);
        setDisplayMessage('');
        getLoggedInUserProfileDetails();
      }, 1500);
    };

    if (updateProfileStatus === 'SUCCESS') {
      handleSuccess(updateProfileMessage);
    } else if (updateProfileStatus === 'FAILED') {
      handleFailure(updateProfileMessage);
    }

    if (
      switchRoleStatus === 'SUCCESS' &&
      switchRoleMessage === 'Successfully switched to hospital account.'
    ) {
      const useSession = sessionStorage.getItem('role') !== null;
      const storage = useSession ? sessionStorage : localStorage;

      if (switchRoleData?.role) {
        storage.setItem('role', switchRoleData.role);
      }
      if (switchRoleData?._id) {
        storage.setItem('userId', switchRoleData._id);
      }
      if (accessToken) {
        storage.setItem('token', accessToken);
      }
      if (switchPacsResult != null) {
        storage.setItem('pacsDetails', JSON.stringify(switchPacsResult));
      }

      setTimeout(() => {
        window.location.reload();
      }, 100);
    } else if (switchRoleStatus === 'FAILED') {
      handleFailure(switchRoleMessage);
    }

    return () => {
      dispatch(clearState());
    };
  }, [
    updateProfileStatus,
    updateProfileMessage,
    switchRoleStatus,
    switchRoleMessage,
    accessToken,
    switchRoleData,
    switchPacsResult,
  ]);

  const handleSwitchToAdmin = () => {
    const payload = {
      email: sessionStorage.getItem('email') || localStorage.getItem('email'),
      loggedInId: sessionStorage.getItem('userId') || localStorage.getItem('userId'),
      hospitalId: sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId'),
      action: 'switch',
    };
    dispatch(getUserDocApi(payload));
    setSwitchToAdmin(prev => !prev);
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const switchToggleSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: T.teal },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: T.teal,
      opacity: 0.5,
    },
  };

  return (
    <>
      <ProfilePageShell>
        <Formik
          initialValues={myProfileInitialValues}
          validationSchema={myProfileValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
            <Form autoComplete="off">
              <ProfileContent>
                <ProfileHead
                  title="Update Profile Information"
                  onCancel={handleCancel}
                  submitDisabled={isSubmitting}
                />

                <ProfileSection
                  title="Profile Details"
                  action={
                    <>
                      {switchRoleStatus === 'SUCCESS' && isSonographer && (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={switchToAdmin}
                              onChange={handleSwitchToAdmin}
                              sx={switchToggleSx}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink2 }}>
                              Switch to Admin
                            </Typography>
                          }
                        />
                      )}
                      {switchRoleStatus === 'SUCCESS' && IsClinicAdmin && (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={switchToAdmin}
                              onChange={handleSwitchToAdmin}
                              sx={switchToggleSx}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink2 }}>
                              Switch to Sonographer
                            </Typography>
                          }
                        />
                      )}
                    </>
                  }
                >
                  {/* anti-autofill decoys (kept from the original) */}
                  <input
                    type="text"
                    name="fakeEmail"
                    autoComplete="off"
                    style={{ display: 'none' }}
                  />
                  <input
                    type="password"
                    name="fakePassword"
                    autoComplete="new-password"
                    style={{ display: 'none' }}
                  />

                  <ProfileGrid>
                    <ProfileField
                      label="Name"
                      required
                    >
                      <TextField
                        fullWidth
                        name="name"
                        placeholder="Name"
                        value={values?.name || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!(touched.name && errors.name)}
                        helperText={(touched.name && (errors.name as string)) || ''}
                        sx={inputSx}
                      />
                    </ProfileField>

                    <ProfileField label="Role">
                      <TextField
                        fullWidth
                        name="role"
                        placeholder="Role"
                        value={values?.role || ''}
                        disabled
                        sx={inputSx}
                      />
                    </ProfileField>

                    <ProfileField label="Email Address">
                      <TextField
                        fullWidth
                        name="email"
                        placeholder="Email Address"
                        value={values?.email || ''}
                        disabled
                        autoComplete="off"
                        sx={inputSx}
                      />
                    </ProfileField>

                    <ProfileField label="Contact Number">
                      <ProfilePhoneField
                        name="contactNumber"
                        value={values?.contactNumber}
                        countryCode={countryCode}
                        onCountryCodeChange={setCountryCode}
                        onChange={digits => setFieldValue('contactNumber', digits)}
                        onBlur={handleBlur}
                        error={(touched.contactNumber && (errors.contactNumber as string)) || ''}
                      />
                    </ProfileField>

                    {/* Clip playback speed: a personal preference, so it sits on the
                        profile rather than in admin. */}
                    {!IsClinicAdmin && (
                      <ProfileField
                        label="Default clip speed"
                        hint="The speed clips open at for you. A speed saved on an individual clip still wins."
                      >
                        <Select
                          fullWidth
                          name="defaultPlaybackRate"
                          value={values?.defaultPlaybackRate ?? ''}
                          onChange={handleChange}
                          displayEmpty
                          sx={inputSx}
                        >
                          <MenuItem value="">Automatic</MenuItem>
                          {PLAYBACK_SPEEDS.map(speed => (
                            <MenuItem
                              key={speed}
                              value={String(speed)}
                            >
                              {speed}x
                            </MenuItem>
                          ))}
                        </Select>
                      </ProfileField>
                    )}
                  </ProfileGrid>
                </ProfileSection>
              </ProfileContent>
            </Form>
          )}
        </Formik>
      </ProfilePageShell>
      <Loader isLoading={loading || updateProfileLoading || switchRoleLoading} />
      <Success
        isSuccessModalOpen={OpenSuccessModal}
        handleCloseModal={() => setOpenSuccessModal(false)}
        title={displayMessage}
      />
      <Failed
        isFailedModalOpen={OpenFailureModal}
        handleCloseModal={() => setOpenFailureModal(false)}
        title={displayMessage}
      />
    </>
  );
};

export default MyProfile;
